/**
 * Global Image Cache Service
 * 
 * Fetches images only once globally and caches them as data URLs in localStorage.
 * Prevents repeated network requests when PreviewPageWYSIWYG or other components
 * generate multiple PDFs or re-render.
 * 
 * Usage:
 *   const dataUrl = await ImageCache.get('https://example.com/image.png');
 *   // subsequent calls with same URL return cached data URL immediately
 */

class ImageCacheService {
  constructor() {
    this.cache = new Map(); // URL -> Promise<dataUrl>
    this.memoryCache = new Map(); // URL -> dataUrl (fast access)
    this.failed = new Set(); // URL -> boolean (failed fetch)
  }

  /**
   * Fetch an image and return it as a data URL.
   * If already cached, return immediately.
   * If fetch is in progress, wait for it.
   * @param {string} url - Image URL
   * @returns {Promise<string>} - data: URL or null on error
   */
  async get(url) {
    if (!url) return null;

    // 0. Check if previously failed
    if (this.failed.has(url)) {
      return null;
    }

    // 1. Check in-memory cache
    if (this.memoryCache.has(url)) {
      return this.memoryCache.get(url);
    }

    // 2. Check localStorage
    try {
      const storageKey = `ImageCache::${url}`;
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        this.memoryCache.set(url, cached);
        return cached;
      }
    } catch (e) {
      // ignore localStorage access errors
    }

    // 3. If a fetch is in progress, wait for it
    if (this.cache.has(url)) {
      return this.cache.get(url);
    }

    // 4. Start new fetch and cache the promise
    const fetchPromise = this._fetchAsDataUrl(url);
    this.cache.set(url, fetchPromise);

    try {
      const dataUrl = await fetchPromise;

      // Update memory cache
      this.memoryCache.set(url, dataUrl);

      // Update localStorage
      try {
        const storageKey = `ImageCache::${url}`;
        localStorage.setItem(storageKey, dataUrl);
      } catch (e) {
        if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
          console.warn('ImageCache: localStorage quota exceeded, clearing old entries might be needed');
          // Optional: clear non-image items or implement LRU here if strictly required
          // For now, we just don't cache this item in storage, but it remains in memory
        }
      }

      this.cache.delete(url); // clean up promise cache
      return dataUrl;
    } catch (error) {
      this.cache.delete(url);
      this.failed.add(url); // Mark as failed to prevent retry loops
      console.warn(`ImageCache: failed to fetch ${url}`, error);
      return null;
    }
  }

  /**
   * Synchronous peek to check if an URL has already been cached (returns dataUrl or null)
   */
  peek(url) {
    if (!url) return null;
    if (this.memoryCache.has(url)) return this.memoryCache.get(url);
    try {
      return localStorage.getItem(`ImageCache::${url}`);
    } catch (_) {
      return null;
    }
  }

  async _fetchAsDataUrl(url) {
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      return url; // already a data or blob URL
    }

    // Try a simple CORS fetch first
    let response = null;
    try {
      response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit',
        cache: 'force-cache',
      });
    } catch (firstErr) {
      // network-level failure - we'll try an authenticated fetch below if possible
      response = null;
    }

    // If unauthenticated fetch failed or returned non-ok, try again with Authorization header
    if (!response || !response.ok) {
      try {
        let token = null;
        try { token = localStorage.getItem('token'); } catch (_) { token = null; }
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        response = await fetch(url, {
          mode: 'cors',
          credentials: 'omit',
          cache: 'force-cache',
          headers,
        });
      } catch (secondErr) {
        // both attempts failed
        throw new Error(`Failed to fetch image: ${url}`);
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch image (${response.status}): ${url}`);
    }

    const blob = await response.blob();

    // If this is a PDF, render first page to a PNG via pdfjs and return PNG data URL
    const contentType = response.headers.get && response.headers.get('content-type') ? response.headers.get('content-type') : blob.type || '';
    const isPdf = /pdf/i.test(contentType) || /\.pdf($|\?)/i.test(url);
    if (isPdf && typeof window !== 'undefined' && window.document) {
      try {
        // dynamic import to avoid loading pdfjs when not needed
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf');
        // Use CDN worker as fallback — ensures worker is available
        try {
          pdfjs.GlobalWorkerOptions.workerSrc = pdfjs.GlobalWorkerOptions.workerSrc || 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.7.107/pdf.worker.min.js';
        } catch (_) { /* ignore */ }

        const arrayBuffer = await blob.arrayBuffer();
        const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        await page.render({ canvasContext: ctx, viewport }).promise;
        const pngDataUrl = canvas.toDataURL('image/png');
        return pngDataUrl;
      } catch (pdfErr) {
        console.warn('ImageCache: PDF render failed, falling back to data URL', pdfErr);
        // fallback to raw data URL below
      }
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Batch fetch multiple images in parallel
   * @param {string[]} urls - Array of image URLs
   * @returns {Promise<Object>} - { url: dataUrl }
   */
  async getBatch(urls) {
    const results = {};
    const promises = urls.map(url =>
      this.get(url).then(dataUrl => {
        results[url] = dataUrl;
      })
    );
    await Promise.all(promises);
    return results;
  }

  /**
   * Clear all cached data (e.g., on logout or app reset)
   */
  clear() {
    this.cache.clear();
    this.memoryCache.clear();
    try {
      // Clear only ImageCache keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ImageCache::')) {
          localStorage.removeItem(key);
        }
      });
    } catch (_) {
      // ignore
    }
  }

  /**
   * Get cache size for debugging
   */
  size() {
    return this.memoryCache.size;
  }
}

// Export singleton instance
const imageCacheInstance = new ImageCacheService();
export default imageCacheInstance;
